import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { DataStoreService, WINDOW } from '@kservice';
import { HttpStatusCode } from '@ktypes/enums';
import { DeviceDetectorService } from 'ngx-device-detector';

@Injectable({
  providedIn: 'root',
})
export class CustomErrorHandler implements ErrorHandler {
  constructor(
    private _dataStoreService: DataStoreService,
    private _deviceDetectorService: DeviceDetectorService,
    private _ngZone: NgZone,
    private _router: Router,
    @Inject(WINDOW) private _window: Window
  ) {}

  _console: Console = console;

  handleError(error: any) {
    const originalError = findOriginalError(error);
    const context = findContext(error);
    // Note: Browser consoles show the place from where console.warn was called.
    // We can use this to give users additional information about the error.
    const errorLogger = getErrorLogger(error);
    const chunkFailedMessage = /Loading chunk \d+ failed/;

    // If this is a Chunk load error, reload the page
    if (chunkFailedMessage.test((error as Error).message)) {
      this._window.location.reload();
    }

    errorLogger(this._console, `ERROR`, error);
    if (originalError) {
      errorLogger(this._console, `ORIGINAL ERROR`, originalError);
    }
    if (context) {
      errorLogger(this._console, 'ERROR CONTEXT', context);
    }

    if (
      (error as HttpErrorResponse).status &&
      ![
        HttpStatusCode.BAD_REQUEST,
        HttpStatusCode.UNAUTHORIZED,
        HttpStatusCode.PAYMENT_REQUIRED,
        HttpStatusCode.FORBIDDEN,
        HttpStatusCode.NOT_FOUND,
      ].includes((error as HttpErrorResponse).status)
    ) {
      void this._ngZone.run(() => this._router.navigate(['error']));
    }
  }
}

export const ERROR_DEBUG_CONTEXT = 'ngDebugContext';
export const ERROR_ORIGINAL_ERROR = 'ngOriginalError';
export const ERROR_LOGGER = 'ngErrorLogger';

function findContext(error: Error): Error {
  if (error) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return getDebugContext(error) ? getDebugContext(error) : findContext(getOriginalError(error));
  }

  return null;
}

function findOriginalError(error: Error): Error {
  let e = getOriginalError(error);
  while (e && getOriginalError(e)) {
    e = getOriginalError(e);
  }

  return e;
}

function getDebugContext(error: Error) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
  return (error as any)[ERROR_DEBUG_CONTEXT];
}

function getOriginalError(error: Error): Error {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
  return (error as any)[ERROR_ORIGINAL_ERROR];
}

function getErrorLogger(error: Error): (console: Console, ...values: any[]) => void {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
  return (error as any)[ERROR_LOGGER] || defaultErrorLogger;
}

function defaultErrorLogger(console: Console, ...values: any[]) {
  console.error(...values);
}
